"use client";

import { create } from "zustand";

export type OnboardingStep =
  | "idle"
  | "accessCodeEntry"
  | "activating"
  | "done"
  | "error";

interface OnboardingError {
  title: string;
  description?: string;
  recoverTo: "accessCodeEntry" | "idle";
}

interface OnboardingState {
  accessCode: string;
  dismissedPubkeys: Set<string>;
  error: OnboardingError | null;
  isModalOpen: boolean;
  pubkey: string | null;
  step: OnboardingStep;
}

interface OnboardingActions {
  clearError: () => void;
  dismissModal: () => void;
  resetDismissed: (pubkey: string) => void;
  setAccessCode: (accessCode: string) => void;
  setError: (error: OnboardingError) => void;
  setStep: (step: Exclude<OnboardingStep, "error">) => void;
  startActivation: () => void;
  startWithAccessCodeEntry: (
    pubkey: string,
    options?: { resetDismissed?: boolean }
  ) => void;
  wasDismissed: (pubkey: string) => boolean;
}

export const useOnboardingStore = create<OnboardingState & OnboardingActions>(
  (set, get) => ({
    accessCode: "",
    dismissedPubkeys: new Set<string>(),
    error: null,
    isModalOpen: false,
    pubkey: null,
    step: "idle",

    clearError: () =>
      set((state) => {
        const recoverTo = state.error?.recoverTo;
        if (recoverTo === undefined) return { error: null };
        return {
          error: null,
          step: recoverTo,
          isModalOpen: recoverTo !== "idle",
        };
      }),

    dismissModal: () =>
      set((state) => {
        const dismissedPubkeys = new Set(state.dismissedPubkeys);
        if (state.pubkey !== null) dismissedPubkeys.add(state.pubkey);
        return {
          accessCode: "",
          dismissedPubkeys,
          error: null,
          isModalOpen: false,
          pubkey: null,
          step: "idle",
        };
      }),

    resetDismissed: (pubkey) =>
      set((state) => {
        if (!state.dismissedPubkeys.has(pubkey)) return state;
        const dismissedPubkeys = new Set(state.dismissedPubkeys);
        dismissedPubkeys.delete(pubkey);
        return { dismissedPubkeys };
      }),

    setAccessCode: (accessCode) => set({ accessCode }),

    setError: (error) =>
      set({
        error,
        isModalOpen: true,
        step: "error",
      }),

    setStep: (step) =>
      set({
        error: null,
        isModalOpen: step !== "idle",
        step,
      }),

    startActivation: () =>
      set((state) => {
        if (state.pubkey === null) {
          return state;
        }
        return {
          error: null,
          isModalOpen: true,
          step: "activating",
        };
      }),

    startWithAccessCodeEntry: (pubkey, options) =>
      set((state) => {
        const dismissedPubkeys = new Set(state.dismissedPubkeys);
        if (options?.resetDismissed === true) {
          dismissedPubkeys.delete(pubkey);
        }
        return {
          accessCode: state.pubkey === pubkey ? state.accessCode : "",
          dismissedPubkeys,
          error: null,
          isModalOpen: true,
          pubkey,
          step: "accessCodeEntry",
        };
      }),

    wasDismissed: (pubkey) => get().dismissedPubkeys.has(pubkey),
  })
);
